import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, Check, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const brl = (n) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(n || 0));

export default function PayMaster() {
  const [params] = useSearchParams();
  const amount = Number(params.get("valor") || 0);
  const desc = params.get("desc") || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!amount || amount <= 0) { setErr("Valor inválido"); return; }
    axios.get(`${API}/public/admin-pix`, { params: { amount, description: desc } })
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Erro ao gerar PIX"));
  }, [amount, desc]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.brcode);
      setCopied(true);
      setTimeout(()=>setCopied(false), 1800);
      toast.success("Chave copiada!");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white dark:from-[#0A0F19] dark:to-[#050810] flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-7 border-[#E5E5E5] dark:border-white/10 shadow-xl">
        <div className="flex items-center gap-2 text-emerald-600 mb-1">
          <ShieldCheck size={16}/>
          <span className="label-tiny">Pagamento PIX</span>
        </div>
        <h1 className="font-display text-3xl tracking-tight">Gestão Expert</h1>
        {desc && <p className="text-sm text-neutral-500 mt-1" data-testid="pay-description">{desc}</p>}

        <div className="my-6 p-5 rounded-md bg-emerald-600 text-white">
          <p className="text-xs uppercase tracking-wider opacity-90">Valor</p>
          <p className="font-display text-4xl mt-1" data-testid="pay-amount">{brl(amount)}</p>
        </div>

        {err && <div className="text-sm text-red-600 p-3 bg-red-50 rounded">{err}</div>}

        {!data && !err && (
          <div className="flex items-center justify-center py-12 text-neutral-400">
            <Loader2 size={20} className="animate-spin"/>
          </div>
        )}

        {data && (
          <>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-neutral-500">Beneficiário</span><span className="font-medium">{data.pix_holder}</span></div>
              {data.pix_type && <div className="flex justify-between"><span className="text-neutral-500">Tipo da chave</span><span className="font-medium">{data.pix_type}</span></div>}
              <div className="flex justify-between"><span className="text-neutral-500">Chave PIX</span><span className="font-mono text-xs truncate ml-2 max-w-[60%]" title={data.pix_key}>{data.pix_key}</span></div>
              {data.pix_city && <div className="flex justify-between"><span className="text-neutral-500">Cidade</span><span className="font-medium">{data.pix_city}</span></div>}
            </div>

            <div className="mt-5 p-3 rounded-md bg-neutral-50 dark:bg-white/5 border border-[#E5E5E5] dark:border-white/10">
              <p className="label-tiny mb-2">PIX Copia e Cola</p>
              <p className="font-mono text-[10px] break-all text-neutral-700 dark:text-neutral-300 leading-relaxed" data-testid="brcode-text">{data.brcode}</p>
            </div>

            <Button
              onClick={copy}
              className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-base"
              data-testid="copy-brcode-button"
            >
              {copied ? <><Check size={16} className="mr-2"/> Chave copiada!</> : <><Copy size={16} className="mr-2"/> Copiar chave PIX</>}
            </Button>

            <p className="text-[11px] text-neutral-500 text-center mt-3 leading-relaxed">
              Cole no app do seu banco em PIX → "Pix Copia e Cola" para concluir o pagamento.
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
